import { useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BRAND } from "@/lib/brand";
import { ShieldCheck, Copyright, Scale } from "lucide-react";
import { useCanonical } from "@/lib/useCanonical";

/**
 * Public IP / trademarks notice. Owned and maintained by Myelina Health.
 * States what marks are claimed, what is not, and how to request
 * permission to reference the brand. This page is a diligence
 * artifact (buyers ask "what IP do you own?") and a growth hygiene
 * artifact (partners need permitted-use rules).
 */
const Trademarks = () => {
  useCanonical({ path: "/trademarks" });

  useEffect(() => {
    document.title = "Trademarks & IP | Myelina Health";
    const desc = document.querySelector('meta[name="description"]');
    const prev = desc?.getAttribute("content") ?? null;
    desc?.setAttribute(
      "content",
      "Trademarks, brand marks, and IP ownership for Myelina Health and the Myelina agent, permitted uses, licensing, and contact.",
    );
    return () => {
      if (prev !== null) desc?.setAttribute("content", prev);
    };
  }, []);

  return (
    <main className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-16">
        <p className="text-[12px] text-muted-foreground uppercase tracking-wider mb-2">Intellectual property</p>
        <h1 className="font-heading text-3xl md:text-4xl font-bold mb-3">Trademarks &amp; IP</h1>
        <p className="text-[15px] text-muted-foreground leading-relaxed mb-10">
          This page is maintained by {BRAND.legalName} to answer common
          questions about our brand marks, permitted uses, and how to
          request licensing. It is app-owned editable content, not an
          independent legal opinion.
        </p>

        <div className="space-y-4">
          <Card className="rounded-2xl border-border/60 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base font-semibold">
                <Copyright className="h-4 w-4 text-primary" /> Our marks
              </CardTitle>
            </CardHeader>
            <CardContent className="text-[14px] leading-relaxed text-muted-foreground space-y-3">
              <p>
                <strong className="text-foreground">Myelina Health</strong> is the wordmark used for the
                company, product, and website. The tree logo (the "Myelina
                mark") is our associated design mark and is used across the
                app, favicon, and marketing surfaces.
              </p>
              <p>
                <strong className="text-foreground">Myelina</strong> is the name of the in-product
                AI energy companion feature.
              </p>
              <p>
                <strong className="text-foreground">"Predict your energy. Empower your life."</strong>{" "}
                is our brand tagline.
              </p>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border/60 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base font-semibold">
                <ShieldCheck className="h-4 w-4 text-primary" /> Permitted uses
              </CardTitle>
            </CardHeader>
            <CardContent className="text-[14px] leading-relaxed text-muted-foreground space-y-2">
              <p>You may, without asking, do the following:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Refer to us by name in editorial coverage, reviews, or research citations.</li>
                <li>Link to any public page on {BRAND.domain}.</li>
                <li>Reference the Myelina agent by name when writing about how you use her.</li>
              </ul>
              <p className="pt-2">You may <em>not</em>, without written permission:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Use our marks in product names, domain names, or company names.</li>
                <li>Modify, recolor, or redraw the tree logo.</li>
                <li>Imply endorsement, partnership, or certification we have not agreed to in writing.</li>
              </ul>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border/60 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base font-semibold">
                <Scale className="h-4 w-4 text-primary" /> Ownership &amp; data-processing agreements
              </CardTitle>
            </CardHeader>
            <CardContent className="text-[14px] leading-relaxed text-muted-foreground space-y-3">
              <p>
                Application source code, product design, brand assets, and
                the Myelina agent behaviour are owned by {BRAND.legalName}.
                Third-party components are used under their respective
                open-source licences and are documented in our repository.
              </p>
              <p>
                Data-processing agreements (DPAs) are available on request
                for clinicians, research partners, and enterprise
                evaluators. See{" "}
                <a href="/for-clinicians/data-handling" className="text-primary underline">
                  data handling for clinicians
                </a>{" "}
                for our standard terms.
              </p>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border/60 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">Contact</CardTitle>
            </CardHeader>
            <CardContent className="text-[14px] leading-relaxed text-muted-foreground">
              Licensing or permitted-use questions:{" "}
              <a href="mailto:legal@myelinahealth.com" className="text-primary underline">
                legal@myelinahealth.com
              </a>
              . General support:{" "}
              <a href="mailto:support@myelinahealth.com" className="text-primary underline">
                support@myelinahealth.com
              </a>
              .
            </CardContent>
          </Card>
        </div>

        <p className="mt-10 text-[11px] text-muted-foreground">
          Last updated {new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}.
        </p>
      </div>
    </main>
  );
};

export default Trademarks;
